import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  BadgePercent,
  Bell,
  CarTaxiFront,
  ChevronRight,
  Clock3,
  Droplets,
  Flower2,
  Grid3X3,
  Heart,
  Home,
  MapPin,
  Mic,
  Minus,
  Package,
  Pill,
  Plus,
  ScanLine,
  Search,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Star,
  Timer,
  Truck,
  UserRound,
  Utensils,
  Wrench,
  X,
} from "lucide-react";
import "./sdui-home.css";

const iconRegistry = {
  BadgeCheck,
  BadgePercent,
  CarTaxiFront,
  Droplets,
  Flower2,
  Grid3X3,
  Home,
  MapPin,
  Mic,
  Package,
  Pill,
  ScanLine,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Timer,
  Truck,
  UserRound,
  Utensils,
  Wrench,
};

const DynamicIcon = ({ name, size = 20 }) => {
  const Icon = iconRegistry[name] || ShoppingBag;

  return <Icon size={size} aria-hidden="true" />;
};

const formatMoney = (value) => `Rs ${value}`;

const layoutVars = (layout = {}) => ({
  "--grid-mobile": layout.mobile || 2,
  "--grid-tablet": layout.tablet || layout.mobile || 3,
  "--grid-laptop": layout.laptop || layout.tablet || 4,
  "--grid-desktop": layout.desktop || layout.laptop || 5,
});

const firstValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== "");

const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const getGradientDirection = (direction = "135deg") => {
  const directions = {
    left_to_right: "90deg",
    right_to_left: "270deg",
    top_to_bottom: "180deg",
    bottom_to_top: "0deg",
    diagonal: "135deg",
  };

  return directions[direction] || direction;
};

const buildGradient = (gradient = {}, fallback) => {
  if (typeof gradient === "string") return gradient;
  if (gradient?.enabled === false) return fallback;
  if (!gradient?.start || !gradient?.end) return fallback;

  return `linear-gradient(${getGradientDirection(gradient.direction)}, ${gradient.start}, ${gradient.end})`;
};

const themeVars = (theme = {}, screenConfig = {}) => {
  const colors = theme.colors || {};
  const gradients = theme.gradients || {};
  const radius = theme.radius || {};
  const headerConfig = asObject(screenConfig.header);
  const footerConfig = asObject(screenConfig.footer);
  const buttonConfig = asObject(screenConfig.button);
  const bottomNavConfig = asObject(screenConfig.bottomNav);
  const header = {
    ...asObject(theme.header),
    ...asObject(screenConfig.headerStyle),
    ...headerConfig,
    ...asObject(headerConfig.style),
  };
  const search = {
    ...asObject(theme.search),
    ...asObject(screenConfig.searchStyle),
    ...asObject(headerConfig.search),
    ...asObject(headerConfig.searchStyle),
  };
  const button = {
    ...asObject(theme.button),
    ...asObject(theme.buttons),
    ...buttonConfig,
    ...asObject(buttonConfig.style),
    ...asObject(screenConfig.buttonStyle),
  };
  const bottomNav = {
    ...asObject(theme.footer),
    ...asObject(theme.bottomNav),
    ...footerConfig,
    ...asObject(footerConfig.style),
    ...bottomNavConfig,
    ...asObject(bottomNavConfig.style),
    ...asObject(screenConfig.footerStyle),
    ...asObject(screenConfig.bottomNavStyle),
  };
  const primary = colors.primary || "#6338d6";
  const primaryDark = colors.primaryDark || "#3d1aa0";
  const surface = colors.surface || "#ffffff";
  const text = colors.text || "#172118";
  const muted = colors.muted || "#657063";
  const border = colors.border || "#dfe8dc";
  const heroGradient =
    gradients.hero || `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 100%)`;
  const headerBackground = buildGradient(
    header.gradient || gradients.header,
    firstValue(header.background, header.backgroundColor, colors.header, heroGradient)
  );
  const footerBackground = buildGradient(
    bottomNav.gradient || gradients.footer,
    firstValue(bottomNav.background, bottomNav.backgroundColor, surface)
  );

  return {
    "--sdui-bg": colors.background || "#f6f8f3",
    "--sdui-surface": surface,
    "--sdui-surface-alt": colors.surfaceAlt || "#fff7e2",
    "--sdui-text": text,
    "--sdui-muted": muted,
    "--sdui-primary": primary,
    "--sdui-primary-dark": primaryDark,
    "--sdui-secondary": colors.secondary || "#ffb020",
    "--sdui-accent": colors.accent || "#f35b42",
    "--sdui-border": border,
    "--sdui-radius-sm": radius.sm || "6px",
    "--sdui-radius-md": radius.md || "8px",
    "--sdui-radius-lg": radius.lg || radius.md || "16px",
    "--sdui-page-gradient": gradients.page || "var(--sdui-bg)",
    "--sdui-hero-gradient": heroGradient,
    "--sdui-header-bg": headerBackground,
    "--sdui-header-text":
      firstValue(header.text_color, header.textColor, header.color, colors.headerText, "#ffffff"),
    "--sdui-header-muted":
      firstValue(header.muted_color, header.mutedColor, colors.headerMuted, "rgba(255, 255, 255, 0.84)"),
    "--sdui-header-icon-bg":
      firstValue(header.icon_background, header.iconBackground, "rgba(255, 255, 255, 0.18)"),
    "--sdui-header-icon-color":
      firstValue(header.icon_color, header.iconColor, "#ffffff"),
    "--sdui-header-action-bg":
      firstValue(header.action_background, header.actionBackground, "rgba(255, 255, 255, 0.14)"),
    "--sdui-search-bg": firstValue(search.background, search.backgroundColor, surface),
    "--sdui-search-text": firstValue(search.text_color, search.textColor, text),
    "--sdui-search-placeholder":
      firstValue(search.placeholder_color, search.placeholderColor, "#7d877b"),
    "--sdui-search-icon": firstValue(search.icon_color, search.iconColor, muted),
    "--sdui-search-border": firstValue(search.border_color, search.borderColor, border),
    "--sdui-button-bg": firstValue(button.background, button.backgroundColor, button.primary, primary),
    "--sdui-button-text": firstValue(button.text_color, button.textColor, button.color, "#ffffff"),
    "--sdui-button-border": firstValue(button.border_color, button.borderColor, primary),
    "--sdui-link-color": firstValue(button.link_color, button.linkColor, primary),
    "--sdui-footer-bg": footerBackground,
    "--sdui-footer-text": firstValue(bottomNav.text_color, bottomNav.textColor, bottomNav.color, "#526050"),
    "--sdui-footer-active":
      firstValue(bottomNav.active_color, bottomNav.activeColor, primary),
    "--sdui-footer-raised-bg":
      firstValue(bottomNav.raised_background, bottomNav.raisedBackground, primary),
    "--sdui-footer-raised-text":
      firstValue(bottomNav.raised_text_color, bottomNav.raisedTextColor, "#ffffff"),
    "--sdui-footer-border": firstValue(bottomNav.border_color, bottomNav.borderColor, border),
    "--sdui-footer-hover-bg":
      firstValue(bottomNav.hover_background, bottomNav.hoverBackground, "rgba(99, 56, 214, 0.1)"),
  };
};

const bannerVars = (slide = {}) => {
  const style = slide.style || {};
  const eyebrowStyle = slide.eyebrowStyle || {};
  const discountBadge = slide.discountBadge || {};
  const buttonStyle = slide.primaryAction?.style || {};
  const background = firstValue(style.background, style.backgroundColor, "var(--sdui-primary)");

  return {
    "--hero-slide-bg": background,
    "--hero-slide-gradient": buildGradient(style.gradient, background),
    "--hero-title-color": firstValue(style.titleColor, style.title_color, "#ffffff"),
    "--hero-subtitle-color":
      firstValue(style.subtitleColor, style.subtitle_color, style.descColor, style.desc_color, "rgba(255, 255, 255, 0.88)"),
    "--hero-badge-bg": firstValue(eyebrowStyle.background, "#ffe88a"),
    "--hero-badge-color": firstValue(eyebrowStyle.color, eyebrowStyle.text_color, "#171717"),
    "--hero-discount-bg": firstValue(discountBadge.background, "#fff0a8"),
    "--hero-discount-color": firstValue(discountBadge.color, discountBadge.text_color, "#151515"),
    "--hero-button-bg": firstValue(buttonStyle.background, buttonStyle.backgroundColor, "#ffffff"),
    "--hero-button-color": firstValue(buttonStyle.color, buttonStyle.text_color, buttonStyle.textColor, "#171717"),
    "--hero-image-position": firstValue(slide.imageSet?.position, slide.imagePosition, "right bottom"),
  };
};

const SectionHeader = ({ title, subtitle, action, onAction }) => {
  if (!title && !subtitle) return null;

  return (
    <div className="sdui-section__header">
      <div>
        {title ? <h2>{title}</h2> : null}
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {action ? (
        <button className="sdui-link-button" type="button" onClick={() => onAction(action.action)}>
          {action.label}
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

const ProductCard = ({ product, quantity = 0, onAction }) => (
  <article className="sdui-product-card">
    <button
      className="sdui-icon-button sdui-product-card__wish"
      type="button"
      aria-label={`Save ${product.name}`}
      title={`Save ${product.name}`}
      onClick={() =>
        onAction({ type: "SHOW_MESSAGE", message: `${product.name} added to wishlist` })
      }
    >
      <Heart size={16} aria-hidden="true" />
    </button>

    {product.badge ? <span className="sdui-product-card__badge">{product.badge}</span> : null}

    <button
      className="sdui-product-card__image"
      type="button"
      aria-label={`Open ${product.name}`}
      onClick={() => onAction(product.detailAction || { type: "NAVIGATE", path: "/shop" })}
    >
      <img src={product.image} alt={product.name} />
    </button>

    <div className="sdui-product-card__body">
      <div className="sdui-product-card__rating">
        <Star size={13} fill="currentColor" aria-hidden="true" />
        {product.rating}
      </div>
      <h3>{product.name}</h3>
      <p>{product.subtitle}</p>
      <div className="sdui-product-card__buy">
        <div className="sdui-product-card__price">
          <strong>{formatMoney(product.price)}</strong>
          {product.mrp ? <span>{formatMoney(product.mrp)}</span> : null}
        </div>
        {quantity > 0 ? (
          <div className="sdui-stepper" aria-label={`${product.name} quantity`}>
            <button
              type="button"
              aria-label={`Remove ${product.name}`}
              onClick={() => onAction({ type: "REMOVE_FROM_CART", payload: { productId: product.id } })}
            >
              <Minus size={14} aria-hidden="true" />
            </button>
            <span>{quantity}</span>
            <button type="button" aria-label={`Add ${product.name}`} onClick={() => onAction(product.action)}>
              <Plus size={14} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <button className="sdui-add-button" type="button" onClick={() => onAction(product.action)}>
            ADD
          </button>
        )}
      </div>
    </div>
  </article>
);

const SduiTopBar = ({ header = {}, cartCount, onAction, onSearch }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const scannerVideoRef = useRef(null);
  const scannerFrameRef = useRef(null);
  const scannerStreamRef = useRef(null);
  const scannerDetectorRef = useRef(null);
  const recognitionRef = useRef(null);

  const showToolMessage = useCallback(
    (message) => onAction({ type: "SHOW_MESSAGE", message }),
    [onAction]
  );

  const submitSearch = useCallback(
    (query) => {
      const nextQuery = String(query || "").trim();

      if (!nextQuery) {
        showToolMessage("Search text is empty");
        return;
      }

      onSearch(nextQuery);
    },
    [onSearch, showToolMessage]
  );

  const stopScanner = useCallback(() => {
    window.cancelAnimationFrame(scannerFrameRef.current);
    scannerFrameRef.current = null;

    if (scannerVideoRef.current) {
      scannerVideoRef.current.srcObject = null;
    }

    if (scannerStreamRef.current) {
      scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      scannerStreamRef.current = null;
    }

    setIsScannerOpen(false);
  }, []);

  useEffect(() => {
    if (!isScannerOpen || !scannerStreamRef.current || !scannerVideoRef.current) return undefined;

    let isActive = true;
    const video = scannerVideoRef.current;
    video.srcObject = scannerStreamRef.current;

    const scanFrame = async () => {
      if (!isActive || !scannerDetectorRef.current) return;

      if (video.readyState >= 2) {
        try {
          const codes = await scannerDetectorRef.current.detect(video);
          const scannedValue = codes?.[0]?.rawValue || codes?.[0]?.displayValue;

          if (scannedValue) {
            setSearchQuery(scannedValue);
            submitSearch(scannedValue);
            stopScanner();
            return;
          }
        } catch (error) {
          // Continue scanning; some frames can fail while the camera is focusing.
        }
      }

      scannerFrameRef.current = window.requestAnimationFrame(scanFrame);
    };

    video
      .play()
      .then(() => {
        scannerFrameRef.current = window.requestAnimationFrame(scanFrame);
      })
      .catch(() => {
        showToolMessage("Camera could not start");
        stopScanner();
      });

    return () => {
      isActive = false;
      window.cancelAnimationFrame(scannerFrameRef.current);
    };
  }, [isScannerOpen, showToolMessage, stopScanner, submitSearch]);

  useEffect(
    () => () => {
      recognitionRef.current?.abort?.();
      if (scannerStreamRef.current) {
        scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    },
    []
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    submitSearch(searchQuery);
  };

  const handleVoiceSearch = () => {
    if (typeof window === "undefined") return;

    if (isListening) {
      recognitionRef.current?.stop?.();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      showToolMessage("Voice search is not supported in this browser");
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = header.voiceLang || "en-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => showToolMessage("Could not hear clearly");
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || "";
      setSearchQuery(transcript);
      submitSearch(transcript);
    };

    recognition.start();
  };

  const handleScannerSearch = async () => {
    if (typeof window === "undefined") return;

    if (!window.navigator?.mediaDevices?.getUserMedia) {
      showToolMessage("Camera scanning is not supported in this browser");
      return;
    }

    if (!window.BarcodeDetector) {
      showToolMessage("Scanner is not supported in this browser");
      return;
    }

    try {
      try {
        scannerDetectorRef.current = new window.BarcodeDetector({
          formats: ["qr_code", "ean_13", "ean_8", "code_128", "code_39", "upc_a", "upc_e"],
        });
      } catch (error) {
        scannerDetectorRef.current = new window.BarcodeDetector();
      }

      scannerStreamRef.current = await window.navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
      setIsScannerOpen(true);
    } catch (error) {
      showToolMessage("Camera permission is required for scanning");
    }
  };

  return (
    <header className="sdui-topbar">
      <div className="sdui-topbar__brand">
        <button
          className="sdui-topbar__location"
          type="button"
          onClick={() => onAction({ type: "SHOW_MESSAGE", message: "Location selector opened" })}
        >
          <span className="sdui-brand-mark">
            <Home size={20} aria-hidden="true" />
          </span>
          <span>
            <strong>{header.locationTitle}</strong>
            <small>
              <MapPin size={13} aria-hidden="true" />
              {header.locationSubtitle}
            </small>
          </span>
        </button>
      </div>

      <form className="sdui-search" onSubmit={handleSubmit}>
        <Search size={18} aria-hidden="true" />
        <input
          name="query"
          type="search"
          value={searchQuery}
          placeholder={header.searchPlaceholder}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        <button
          className={`sdui-icon-button sdui-search__tool ${isListening ? "is-active" : ""}`}
          type="button"
          aria-label={isListening ? "Stop voice search" : "Voice search"}
          title={isListening ? "Listening" : "Voice search"}
          onClick={handleVoiceSearch}
        >
          <Mic size={18} aria-hidden="true" />
        </button>
        <button
          className="sdui-icon-button"
          type="button"
          aria-label="Scan code"
          title="Scan code"
          onClick={handleScannerSearch}
        >
          <ScanLine size={18} aria-hidden="true" />
        </button>
      </form>

      <div className="sdui-topbar__actions">
        {header.deliveryEta ? (
          <span className="sdui-eta">
            <Clock3 size={17} aria-hidden="true" />
            {header.deliveryEta}
          </span>
        ) : null}
        <button
          className="sdui-icon-button sdui-notification-button"
          type="button"
          aria-label="Notifications"
          title="Notifications"
          onClick={() => onAction({ type: "SHOW_MESSAGE", message: "No new notifications" })}
        >
          <Bell size={19} aria-hidden="true" />
          {header.notificationBadge ? <em>{header.notificationBadge}</em> : null}
        </button>
        {header.avatar ? <img className="sdui-avatar" src={header.avatar} alt="Account" /> : null}
        {header.actions?.map((item) => {
          const isCart = item.id === "cart";

          return (
            <button
              className="sdui-action-button"
              type="button"
              key={item.id}
              onClick={() => onAction(item.action)}
            >
              <span>
                <DynamicIcon name={item.icon} size={18} />
                {isCart && cartCount > 0 ? <em>{cartCount}</em> : null}
              </span>
              {item.label}
            </button>
          );
        })}
      </div>

      {isScannerOpen ? (
        <div className="sdui-scanner" role="dialog" aria-modal="true" aria-label="Scan product code">
          <div className="sdui-scanner__panel">
            <video ref={scannerVideoRef} muted playsInline />
            <span className="sdui-scanner__frame" aria-hidden="true" />
            <button type="button" aria-label="Close scanner" title="Close scanner" onClick={stopScanner}>
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
    </header>
  );
};

const HeroBanner = ({ section, onAction }) => {
  const data = section.data || {};

  if (!data.title && !data.subtitle && !data.image) return null;

  return (
    <section
      className="sdui-hero"
      style={{
        "--hero-bg": section.style?.background || "var(--sdui-surface-alt)",
        "--hero-accent": section.style?.accent || "var(--sdui-secondary)",
      }}
    >
      <div className="sdui-hero__content">
        {data.eyebrow ? <span className="sdui-eyebrow">{data.eyebrow}</span> : null}
        <h1>{data.title}</h1>
        <p>{data.subtitle}</p>
        <div className="sdui-hero__actions">
          {data.primaryAction ? (
            <button className="sdui-primary-button" type="button" onClick={() => onAction(data.primaryAction.action)}>
              {data.primaryAction.label}
              <ChevronRight size={17} aria-hidden="true" />
            </button>
          ) : null}
          {data.secondaryAction ? (
            <button className="sdui-secondary-button" type="button" onClick={() => onAction(data.secondaryAction.action)}>
              {data.secondaryAction.label}
            </button>
          ) : null}
        </div>
        {Array.isArray(data.chips) ? (
          <div className="sdui-hero__chips">
            {data.chips.map((chip) => (
              <span key={chip}>{chip}</span>
            ))}
          </div>
        ) : null}
      </div>
      <div className="sdui-hero__media">
        <img src={data.image} alt="" />
      </div>
    </section>
  );
};

const CategoryGrid = ({ section, onAction }) => (
  <section className="sdui-section">
    <SectionHeader title={section.title} subtitle={section.subtitle} onAction={onAction} />
    <div className="sdui-category-grid" style={layoutVars(section.layout)}>
      {section.data?.map((category) => (
        <button
          className="sdui-category-tile"
          type="button"
          key={category.id}
          onClick={() => onAction(category.action)}
        >
          <span>
            <img src={category.image} alt="" />
          </span>
          <strong>{category.label}</strong>
        </button>
      ))}
    </div>
  </section>
);

const PromoStrip = ({ section, onAction }) => (
  <section className="sdui-section sdui-promo-strip" aria-label="Offers">
    {section.data?.map((promo) => (
      <button
        className={`sdui-promo-tile sdui-promo-tile--${promo.tone || "green"}`}
        type="button"
        key={promo.id}
        onClick={() => onAction(promo.action)}
      >
        <span>{promo.label}</span>
        <strong>{promo.description}</strong>
      </button>
    ))}
  </section>
);

const getThemeColor = (themes = {}, themeId, fallback = "var(--sdui-primary)") =>
  themes?.[themeId]?.colors?.primary || fallback;

const HeroCarousel = ({ section, onAction }) => {
  const slides = Array.isArray(section.data) ? section.data : [];

  if (slides.length === 0) return null;

  const slide = slides[0] || {};
  const imageSet = slide.imageSet || {};
  const image = imageSet.desktop || imageSet.tablet || imageSet.mobile || slide.image;
  const discountText =
    slide.discountBadge?.text || slide.discountBadge?.label || slide.badge;

  return (
    <section className="sdui-hero-carousel">
      <div className="sdui-hero-slide" style={bannerVars(slide)}>
        <div className="sdui-hero-slide__content">
          {slide.eyebrow ? <span>{slide.eyebrow}</span> : null}
          <h1>{slide.title}</h1>
          {slide.subtitle ? <p>{slide.subtitle}</p> : null}
          {slide.primaryAction ? (
            <button type="button" onClick={() => onAction(slide.primaryAction.action)}>
              {slide.primaryAction.label}
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {discountText ? <strong className="sdui-hero-slide__badge">{discountText}</strong> : null}
        {image ? (
          <picture className="sdui-hero-slide__picture">
            {imageSet.mobile ? <source media="(max-width: 639px)" srcSet={imageSet.mobile} /> : null}
            {imageSet.tablet ? <source media="(max-width: 991px)" srcSet={imageSet.tablet} /> : null}
            <img className="sdui-hero-slide__image" src={image} alt="" />
          </picture>
        ) : null}
      </div>
      {slides.length > 0 ? (
        <div className="sdui-hero-dots" aria-hidden="true">
          {slides.map((item, index) => (
            <span className={index === 0 ? "active" : ""} key={item.id || index} />
          ))}
        </div>
      ) : null}
    </section>
  );
};

const ServiceGrid = ({ section, themes, onAction }) => (
  <section className="sdui-section">
    <SectionHeader title={section.title} action={section.action} onAction={onAction} />
    <div className="sdui-service-grid" style={layoutVars(section.layout)}>
      {section.data?.map((item) => (
        <button
          className="sdui-service-tile"
          style={{
            "--service-bg": item.bg || "var(--sdui-surface-alt)",
            "--service-color": getThemeColor(themes, item.theme_id),
          }}
          type="button"
          key={item.id}
          onClick={() => onAction(item.action)}
        >
          <span>
            {item.image ? (
              <img src={item.image} alt="" />
            ) : (
              <DynamicIcon name={item.icon} size={34} />
            )}
          </span>
          <strong>{item.label}</strong>
        </button>
      ))}
    </div>
  </section>
);

const OfferCards = ({ section, themes, onAction }) => (
  <section className="sdui-section">
    <SectionHeader title={section.title} action={section.action} onAction={onAction} />
    <div className="sdui-offer-rail">
      {section.data?.map((item) => (
        <button
          className="sdui-offer-card"
          style={{
            "--offer-color": getThemeColor(themes, item.theme_id),
            "--offer-bg": themes?.[item.theme_id]?.colors?.surfaceAlt || "var(--sdui-surface-alt)",
          }}
          type="button"
          key={item.id}
          onClick={() => onAction(item.action)}
        >
          <span>{item.label}</span>
          <strong>{item.title}</strong>
          <small>{item.subtitle}</small>
          {item.image ? <img src={item.image} alt="" /> : null}
          <em>
            <ChevronRight size={18} aria-hidden="true" />
          </em>
        </button>
      ))}
    </div>
  </section>
);

const RecentRail = ({ section, onAction }) => (
  <section className="sdui-section">
    <SectionHeader title={section.title} action={section.action} onAction={onAction} />
    <div className="sdui-recent-rail">
      {section.data?.map((item) => (
        <button className="sdui-recent-card" type="button" key={item.id} onClick={() => onAction(item.action)}>
          {item.image ? <img src={item.image} alt="" /> : null}
          <span>
            <strong>{item.title}</strong>
            <small>{item.subtitle}</small>
            <em>{item.meta}</em>
          </span>
        </button>
      ))}
    </div>
  </section>
);

const StoreRail = ({ section, onAction }) => (
  <section className="sdui-section">
    <SectionHeader title={section.title} action={section.action} onAction={onAction} />
    <div className="sdui-store-rail">
      {section.data?.map((item) => (
        <button className="sdui-store-card" type="button" key={item.id} onClick={() => onAction(item.action)}>
          {item.image ? <img src={item.image} alt="" /> : null}
          <span>
            <strong>{item.title}</strong>
            {item.rating ? (
              <small>
                <Star size={12} fill="currentColor" aria-hidden="true" />
                {item.rating}
              </small>
            ) : null}
          </span>
          <p>{item.subtitle}</p>
          <em>
            <Clock3 size={13} aria-hidden="true" />
            {item.eta}
          </em>
        </button>
      ))}
    </div>
  </section>
);

const BrandRail = ({ section, onAction }) => (
  <section className="sdui-section">
    <SectionHeader title={section.title} action={section.action} onAction={onAction} />
    <div className="sdui-brand-rail">
      {section.data?.map((item) => (
        <button className="sdui-brand-card" type="button" key={item.id} onClick={() => onAction(item.action)}>
          {item.image ? <img src={item.image} alt="" /> : null}
          <strong>{item.label}</strong>
        </button>
      ))}
    </div>
  </section>
);

const TrustStrip = ({ section }) => (
  <section className="sdui-trust-strip">
    {section.data?.map((item) => (
      <div className="sdui-trust-item" key={item.id}>
        <DynamicIcon name={item.icon} size={24} />
        <span>
          <strong>{item.title}</strong>
          <small>{item.subtitle}</small>
        </span>
      </div>
    ))}
  </section>
);

const ProductGrid = ({ section, cart, onAction, rail = false }) => (
  <section className={`sdui-section ${rail ? "sdui-section--rail" : ""}`}>
    <SectionHeader
      title={section.title}
      subtitle={section.subtitle}
      action={{ label: "View all", action: { type: "NAVIGATE", path: "/shop" } }}
      onAction={onAction}
    />
    <div className={rail ? "sdui-product-rail" : "sdui-product-grid"} style={layoutVars(section.layout)}>
      {section.data?.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          quantity={cart[product.id] || 0}
          onAction={onAction}
        />
      ))}
    </div>
  </section>
);

const StoryMosaic = ({ section, onAction }) => (
  <section className="sdui-section">
    <SectionHeader title={section.title} onAction={onAction} />
    <div className="sdui-story-mosaic">
      {section.data?.map((story) => (
        <button className="sdui-story-tile" type="button" key={story.id} onClick={() => onAction(story.action)}>
          <img src={story.image} alt="" />
          <span>
            <strong>{story.title}</strong>
            <small>{story.description}</small>
          </span>
        </button>
      ))}
    </div>
  </section>
);

const sectionRegistry = {
  hero_banner: HeroBanner,
  hero_carousel: HeroCarousel,
  category_grid: CategoryGrid,
  service_grid: ServiceGrid,
  promo_strip: PromoStrip,
  offer_cards: OfferCards,
  recent_rail: RecentRail,
  store_rail: StoreRail,
  brand_rail: BrandRail,
  trust_strip: TrustStrip,
  product_grid: ProductGrid,
  product_rail: (props) => <ProductGrid {...props} rail />,
  story_mosaic: StoryMosaic,
};

const MobileNav = ({ items = [], cartCount, onAction }) => {
  const navItems =
    items.length > 0
      ? items
      : [
          { id: "home", label: "Home", icon: "Home", active: true, action: { type: "NAVIGATE", path: "/home" } },
          { id: "stores", label: "Stores", icon: "MapPin", action: { type: "NAVIGATE", path: "/stores" } },
          { id: "orders", label: "Orders", icon: "Package", action: { type: "NAVIGATE", path: "/orders" } },
          { id: "cart", label: "Cart", icon: "ShoppingCart", action: { type: "NAVIGATE", path: "/cart" } },
        ];

  return (
  <nav
    className="sdui-mobile-nav"
    aria-label="Primary mobile"
    style={{ "--sdui-mobile-nav-count": navItems.length }}
  >
    {navItems.map((item) => (
      <button
        className={`${item.active ? "active" : ""} ${item.raised ? "raised" : ""}`}
        type="button"
        key={item.id}
        style={{
          "--sdui-nav-item-color": item.text_color || item.textColor || item.color,
          "--sdui-nav-active-color": item.active_color || item.activeColor,
          "--sdui-nav-raised-bg": item.background || item.backgroundColor,
          "--sdui-nav-raised-text": item.raised_text_color || item.raisedTextColor || item.text_color || item.textColor,
        }}
        onClick={() => onAction(item.action)}
      >
        <span>
          <DynamicIcon name={item.icon} size={item.raised ? 20 : 18} />
          {item.id === "cart" && cartCount > 0 ? <em>{cartCount}</em> : null}
        </span>
        {item.label}
      </button>
    ))}
  </nav>
  );
};

const SduiHomeRenderer = ({ payload }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({});
  const [message, setMessage] = useState("");
  const [activeThemeId, setActiveThemeId] = useState(payload.theme?.id);
  const toastTimerRef = useRef(null);
  const activeTheme = useMemo(
    () => payload.themes?.[activeThemeId] || payload.theme,
    [activeThemeId, payload.theme, payload.themes]
  );
  const rootVars = useMemo(
    () => themeVars(activeTheme, payload.screenConfig),
    [activeTheme, payload.screenConfig]
  );
  const cartCount = Object.values(cart).reduce((total, quantity) => total + quantity, 0);

  useEffect(() => {
    setActiveThemeId(payload.theme?.id);
  }, [payload.theme?.id]);

  const showMessage = (nextMessage) => {
    setMessage(nextMessage);
    window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setMessage(""), 2400);
  };

  const handleAction = (action) => {
    if (!action) return;

    if (action.type === "NAVIGATE" && action.path) {
      navigate(action.path);
      return;
    }

    if (action.type === "APPLY_THEME") {
      if (action.theme_id && payload.themes?.[action.theme_id]) {
        setActiveThemeId(action.theme_id);
        showMessage(`${payload.themes[action.theme_id].name || action.theme_id} applied`);
      }
      return;
    }

    if (action.type === "ADD_TO_CART") {
      const productId = action.payload?.productId;
      if (!productId) return;

      setCart((currentCart) => ({
        ...currentCart,
        [productId]: (currentCart[productId] || 0) + 1,
      }));
      showMessage("Added to cart");
      return;
    }

    if (action.type === "REMOVE_FROM_CART") {
      const productId = action.payload?.productId;
      if (!productId) return;

      setCart((currentCart) => {
        const nextQuantity = Math.max((currentCart[productId] || 0) - 1, 0);
        const nextCart = { ...currentCart };

        if (nextQuantity === 0) {
          delete nextCart[productId];
        } else {
          nextCart[productId] = nextQuantity;
        }

        return nextCart;
      });
      return;
    }

    if (action.type === "SHOW_MESSAGE") {
      showMessage(action.message || "Done");
    }
  };

  const handleSearch = (queryOrEvent) => {
    let query = queryOrEvent;

    if (queryOrEvent?.preventDefault) {
      queryOrEvent.preventDefault();
      query = new FormData(queryOrEvent.currentTarget).get("query");
    }

    const nextQuery = String(query || "").trim();

    if (!nextQuery) return;
    navigate(`/shop?search=${encodeURIComponent(nextQuery)}`);
  };

  return (
    <div className="sdui-home" style={rootVars}>
      <SduiTopBar
        header={payload.header}
        cartCount={cartCount}
        onAction={handleAction}
        onSearch={handleSearch}
      />
      <main className="sdui-main">
        {payload.sections?.map((section) => {
          const Component = sectionRegistry[section.componentType];

          if (!Component) return null;

          return (
            <Component
              key={section.id}
              section={section}
              cart={cart}
              themes={payload.themes}
              onAction={handleAction}
            />
          );
        })}
      </main>
      <MobileNav items={payload.bottomNav} cartCount={cartCount} onAction={handleAction} />
      {message ? <div className="sdui-toast" role="status">{message}</div> : null}
    </div>
  );
};

export default SduiHomeRenderer;
